import React, { createContext, useContext, useState, useEffect } from 'react';
import i18n from '../i18n'; // Direct import instead of hook
import axios from 'axios';

const LanguageContext = createContext();

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

// Map countries to supported languages
const countryToLanguageMap = {
  // Spanish-speaking countries
  'ES': 'es', 'MX': 'es', 'AR': 'es', 'CO': 'es', 'PE': 'es', 'VE': 'es', 
  'CL': 'es', 'EC': 'es', 'GT': 'es', 'CU': 'es', 'BO': 'es', 'DO': 'es',
  'HN': 'es', 'PY': 'es', 'SV': 'es', 'NI': 'es', 'CR': 'es', 'PA': 'es',
  'UY': 'es', 'GQ': 'es',
  
  // French-speaking countries
  'FR': 'fr', 'BE': 'fr', 'CH': 'fr', 'CA': 'fr', 'LU': 'fr', 'MC': 'fr',
  'CI': 'fr', 'CM': 'fr', 'CD': 'fr', 'MG': 'fr', 'SN': 'fr', 'ML': 'fr',
  'BF': 'fr', 'NE': 'fr', 'TD': 'fr', 'GA': 'fr', 'BJ': 'fr', 'TG': 'fr',
  
  // German-speaking countries
  'DE': 'de', 'AT': 'de', 'LI': 'de',
  
  // Italian-speaking countries
  'IT': 'it', 'SM': 'it', 'VA': 'it',
  
  // Japanese-speaking countries
  'JP': 'ja',
  
  // Portuguese-speaking countries
  'PT': 'pt', 'BR': 'pt', 'AO': 'pt', 'MZ': 'pt', 'CV': 'pt', 'GW': 'pt',
  'ST': 'pt', 'TL': 'pt',
  
  // Chinese-speaking countries/regions
  'CN': 'zh', 'TW': 'zh', 'HK': 'zh', 'SG': 'zh', 'MO': 'zh'
};

export const LanguageProvider = ({ children }) => {
  const [currentLanguage, setCurrentLanguage] = useState('en');
  const [isLoading, setIsLoading] = useState(true);
  const [authToken, setAuthToken] = useState(sessionStorage.getItem('authToken'));
  const [ipDetectedLanguage, setIpDetectedLanguage] = useState(null);

  const API = process.env.REACT_APP_BACKEND_URL;

  // Get auth headers
  const getAuthHeaders = () => {
    const token = sessionStorage.getItem('authToken');
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  // Detect user's country from IP and map to language
  useEffect(() => {
    const detectLanguageFromIP = async () => {
      try {
        // Check if we already detected IP language in this session
        const cachedLanguage = sessionStorage.getItem('ipDetectedLanguage');
        if (cachedLanguage) {
          console.log('Using cached IP-detected language:', cachedLanguage);
          setIpDetectedLanguage(cachedLanguage);
          return;
        }

        console.log('Detecting language from IP via backend...');
        // Use backend endpoint to avoid CORS issues
        const response = await axios.get(`${API}/api/detect-language`, {
          timeout: 5000
        });
        
        const detectedLanguage = response.data.detected_language;
        const detectedCountry = response.data.country_code;
        console.log('Detected country:', detectedCountry, '(', response.data.country_name, ')');
        console.log('Mapped to language:', detectedLanguage);
        
        // Cache the detected language
        sessionStorage.setItem('ipDetectedLanguage', detectedLanguage);
        setIpDetectedLanguage(detectedLanguage);
      } catch (error) {
        console.error('Error detecting language from IP:', error);
        // Default to English if detection fails
        setIpDetectedLanguage('en');
        sessionStorage.setItem('ipDetectedLanguage', 'en');
      }
    };

    detectLanguageFromIP();
  }, [API]);

  // Load user language preference
  useEffect(() => {
    const loadUserLanguage = async () => {
      try {
        // Ensure i18n is initialized
        if (!i18n.isInitialized) {
          await new Promise(resolve => {
            if (i18n.isInitialized) {
              resolve();
            } else {
              i18n.on('initialized', resolve);
            }
          });
        }

        const response = await axios.get(`${API}/api/user/profile`, {
          headers: getAuthHeaders()
        });
        const userLanguage = response.data.language || 'en';
        console.log('Loading user language preference:', userLanguage);
        setCurrentLanguage(userLanguage);
        await i18n.changeLanguage(userLanguage);
        console.log('Successfully changed language to:', userLanguage);
      } catch (error) {
        console.error('Error loading user language:', error);
        // Default to English if error
        setCurrentLanguage('en');
        if (i18n.isInitialized) {
          await i18n.changeLanguage('en');
        }
      } finally {
        setIsLoading(false);
      }
    };

    const initializeDefaultLanguage = async () => {
      try {
        // Ensure i18n is initialized
        if (!i18n.isInitialized) {
          await new Promise(resolve => {
            if (i18n.isInitialized) {
              resolve();
            } else {
              i18n.on('initialized', resolve);
            }
          });
        }
        
        // First check localStorage for saved preference
        const savedLanguage = localStorage.getItem('language');
        if (savedLanguage) {
          console.log('Using saved language preference:', savedLanguage);
          setCurrentLanguage(savedLanguage);
          await i18n.changeLanguage(savedLanguage);
          setIsLoading(false);
          return;
        }
        
        // Then check browser language
        const browserLanguage = navigator.language || navigator.userLanguage;
        if (browserLanguage && browserLanguage.startsWith('es')) {
          console.log('Using browser language (Spanish):', browserLanguage);
          setCurrentLanguage('es');
          await i18n.changeLanguage('es');
          setIsLoading(false);
          return;
        }
        
        // Wait for IP detection to complete if it's still running
        let attempts = 0;
        while (ipDetectedLanguage === null && attempts < 20) {
          await new Promise(resolve => setTimeout(resolve, 100));
          attempts++;
        }
        
        // Use IP-detected language or default to English
        const languageToUse = ipDetectedLanguage || 'en';
        console.log('Initializing default language:', languageToUse);
        setCurrentLanguage(languageToUse);
        await i18n.changeLanguage(languageToUse);
      } catch (error) {
        console.error('Error initializing default language:', error);
        setCurrentLanguage('en');
      } finally {
        setIsLoading(false);
      }
    };

    // Add a delay to ensure authentication has completed
    const timer = setTimeout(() => {
      const currentToken = sessionStorage.getItem('authToken');
      if (currentToken) {
        loadUserLanguage();
      } else {
        initializeDefaultLanguage();
      }
    }, 1000); // Wait 1 second for auth to complete

    return () => clearTimeout(timer);
  }, [API, authToken, ipDetectedLanguage]);

  // Monitor token changes
  useEffect(() => {
    const handleStorageChange = () => {
      const newToken = sessionStorage.getItem('authToken');
      setAuthToken(newToken);
    };

    // Listen for storage events (when token changes in other tabs)
    window.addEventListener('storage', handleStorageChange);
    
    // Also check periodically for token changes in the same tab
    const interval = setInterval(() => {
      const currentToken = sessionStorage.getItem('authToken');
      if (currentToken !== authToken) {
        setAuthToken(currentToken);
      }
    }, 1000);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      clearInterval(interval);
    };
  }, [authToken]);

  // Force reload user language (useful for debugging and ensuring language loads)
  const reloadUserLanguage = async () => {
    const currentToken = sessionStorage.getItem('authToken');
    if (currentToken) {
      try {
        const response = await axios.get(`${API}/api/user/profile`, {
          headers: { Authorization: `Bearer ${currentToken}` }
        });
        const userLanguage = response.data.language || 'en';
        console.log('Reloading user language preference:', userLanguage);
        setCurrentLanguage(userLanguage);
        if (i18n.isInitialized) {
          await i18n.changeLanguage(userLanguage);
          console.log('Successfully reloaded language to:', userLanguage);
        }
      } catch (error) {
        console.error('Error reloading user language:', error);
      }
    }
  };

  // Change language and update i18n
  const changeLanguage = async (languageCode) => {
    try {
      if (i18n.isInitialized) {
        await i18n.changeLanguage(languageCode);
        setCurrentLanguage(languageCode);
        // Save to localStorage for persistence across sessions
        localStorage.setItem('language', languageCode);
        console.log('Language changed to:', languageCode);
      }
    } catch (error) {
      console.error('Error changing language:', error);
    }
  };

  const value = {
    currentLanguage,
    changeLanguage,
    reloadUserLanguage,
    isLoading,
    ipDetectedLanguage
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};

export default LanguageContext;